/**
 * Vehicle API — Secure wrappers for vehicle edge functions
 * 
 * All PII-bearing vehicle operations (VIN, license plate) route through
 * edge functions to ensure encryption/decryption happens server-side.
 */

import { supabase } from '@/integrations/supabase/client';
import type { CustomerVehicle } from '@/lib/customers/types';
import type { VpicDecode } from '../../../supabase/functions/_shared/vehicleProfile/vpic';

interface CreateVehicleInput {
  shop_id: string;
  customer_id?: string | null;
  year: number;
  make: string;
  model: string;
  engine_size?: string | null;
  vin?: string | null;
  license_plate?: string | null;
  gvwr_lbs?: number | null;
}

interface UpdateVehicleInput {
  vehicle_id: string;
  shop_id: string;
  year?: number;
  make?: string;
  model?: string;
  engine_size?: string | null;
  vin?: string | null;
  license_plate?: string | null;
  gvwr_lbs?: number | null;
  customer_id?: string | null;
}

interface VehicleReadResult {
  id: string;
  shop_id: string;
  customer_id: string | null;
  year: number;
  make: string;
  model: string;
  engine_size: string | null;
  vin: string | null;
  license_plate: string | null;
  gvwr_lbs: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Create a new vehicle via vehicle-write edge function.
 * Returns the new vehicle ID.
 */
export async function createVehicle(input: CreateVehicleInput): Promise<string> {
  const { data, error } = await supabase.functions.invoke('vehicle-write', {
    body: input,
  });

  if (error) throw new Error(error.message || 'Failed to create vehicle');
  if (data?.error) throw new Error(data.error);
  return data?.vehicle_id;
}

/** A trusted server decode of a VIN: the canonical normalized facts + the legacy form fields. */
export type ServerVinDecode = Pick<VpicDecode, 'facts' | 'quality' | 'legacyBasics'>;

/**
 * Decode a VIN through the TRUSTED SERVER (vehicle-profile `decode_vin`) — the
 * browser never calls NHTSA. The server leaves a short-lived receipt, so
 * saving this VIN on the vehicle afterwards records the facts with no second
 * NHTSA request. Returns null when NHTSA had nothing to say (Not decoded).
 */
export async function decodeVinOnServer(shop_id: string, vin: string): Promise<ServerVinDecode | null> {
  const { data, error } = await supabase.functions.invoke('vehicle-profile', {
    body: { action: 'decode_vin', shop_id, vin },
  });
  if (error) throw new Error(error.message || 'VIN decode failed');
  if (data?.error) throw new Error(String(data.error));
  return data?.status === 'decoded' && data.decode ? (data.decode as ServerVinDecode) : null;
}

/**
 * Update an existing vehicle via vehicle-write edge function.
 */
export async function updateVehicle(input: UpdateVehicleInput): Promise<string> {
  const { data, error } = await supabase.functions.invoke('vehicle-write', {
    body: input,
  });

  if (error) throw new Error(error.message || 'Failed to update vehicle');
  if (data?.error) throw new Error(data.error);
  return data?.vehicle_id;
}

/**
 * Fetch vehicles for a customer with decrypted VIN/plate.
 * Uses vehicle-read edge function.
 */
export async function fetchVehiclesByCustomer(
  shop_id: string,
  customer_id: string,
): Promise<VehicleReadResult[]> {
  const { data, error } = await supabase.functions.invoke('vehicle-read', {
    body: { shop_id, customer_id },
  });

  if (error) throw new Error(error.message || 'Failed to fetch vehicles');
  if (data?.error) throw new Error(data.error);
  return data?.vehicles || [];
}

/**
 * Fetch a single vehicle by ID with decrypted VIN/plate.
 */
export async function fetchVehicleById(
  shop_id: string,
  vehicle_id: string,
): Promise<VehicleReadResult | null> {
  const { data, error } = await supabase.functions.invoke('vehicle-read', {
    body: { shop_id, vehicle_id },
  });

  if (error) throw new Error(error.message || 'Failed to fetch vehicle');
  if (data?.error) throw new Error(data.error);
  return data?.vehicle || null;
}

/** OCT28 RECOVERY #1 — a vehicle's identity as a board shows it (decrypted server-side by vehicle-read). */
export interface VehicleIdentity {
  id: string;
  vin: string | null;
  license_plate: string | null;
  plate_jurisdiction: string | null;
  /** Fields the server could not decrypt — shown as unavailable, never as ciphertext. */
  unreadable: string[];
}

/** The identities of many vehicles of one shop in one request (vehicle-read `vehicle_ids`; front office + technicians). */
export async function fetchVehicleIdentities(shop_id: string, vehicle_ids: string[]): Promise<VehicleIdentity[]> {
  const { data, error } = await supabase.functions.invoke('vehicle-read', {
    body: { shop_id, vehicle_ids },
  });
  if (error) throw new Error(error.message || 'Failed to fetch vehicle identities');
  if (data?.error) throw new Error(String(data.error));
  return data?.identities || [];
}
